import type { QueryObserverResult, QueryStatus } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  BffShoppingCart,
  BffShoppingCartResponse,
} from '@/core/models/bff-shopping-carts';
import {
  matchesFrameProductId,
  matchesMattressProductId,
  NONE_SUBSCRIPTION_PRODUCT_ID,
} from '@/core/constants/product-ids';
import useFrameQuantityLimit from '@/core/hooks/use-frame-quantity-limit';
import useResolvedCatalogProductIds from '@/core/hooks/use-resolved-catalog-product-ids';
import useBffShoppingCartQuery, {
  queryFn as fetchShoppingCart,
  queryKey as shoppingCartQueryKey,
} from '@/core/queries/bff/shopping-carts/use-bff-shopping-cart-query';
import {
  createBffShoppingCart,
  putBffShoppingCartCoupon,
  putBffShoppingCartProducts,
  putBffShoppingCartSubscriptions,
} from '@/core/services/fetchers';
import { markFreshOrderId } from '@/core/utils/clear-checkout-market-session';
import invalidateStaleShoppingCart from '@/core/utils/invalidate-stale-shopping-cart';
import { isInvalidShoppingCartError } from '@/core/utils/is-invalid-shopping-cart-error';
import { useEvaContext } from '@/ui/context/eva';
import useFeatureFlag from '@/ui/hooks/use-feature-flag';
import useLocaleParams from '@/ui/hooks/use-locale-params';

/** Full `refetch()` result; `data` is the API envelope `{ data: BffShoppingCart }`. */
export type RefetchCartResult =
  QueryObserverResult<BffShoppingCartResponse>;

export type CartMutationOptions = {
  /**
   * Skip writing the mutation response into the React Query cache. Use when
   * batching several adds and calling `refreshCart` once afterward.
   */
  skipRefresh?: boolean;
};

/**
 * Shared across all `useOrder()` callers so concurrent first adds await one POST
 * instead of creating orphaned empty carts.
 */
let createOrderInFlight: Promise<string> | null = null;

/** Survives React closure staleness between createOrder and a same-tick refreshCart. */
let latestKnownCartId: string | null = null;

export type UseOrderReturn = {
  status: QueryStatus;
  orderId: string | null;
  cart: BffShoppingCart | undefined;
  /** Back-compat: minimal amount fields used by UI */
  amounts:
    | {
        currency: string;
        subtotalInclTax: number;
        totalInclTax: number;
        totalDiscounts: number;
        totalShipping: number;
      }
    | undefined;
  setOrderId: (id: string | null) => void;
  createOrder: () => Promise<string>;
  addProduct: (
    productId: string,
    quantity?: number,
    options?: CartMutationOptions,
  ) => Promise<string | null>;
  addSubscriptionProduct: (
    productId: string,
    options?: CartMutationOptions,
  ) => Promise<string | null>;
  removeProduct: (productId: string) => Promise<void>;
  modifyQuantity: (productId: string, quantity: number) => Promise<void>;
  addCoupon: (code: string) => Promise<boolean | undefined>;
  removeCoupon: () => Promise<void>;
  clearOrder: () => void;
  refetchCart: () => Promise<RefetchCartResult>;
  /** Force a single GET and update the cart cache (for batched mutations). */
  refreshCart: () => Promise<BffShoppingCartResponse | undefined>;
  duplicateOrder: () => Promise<void>;
  isProductAvailable: (productId: string, quantity?: number) => boolean;
};

const useOrder = (): UseOrderReturn => {
  const { orderId, setOrderId } = useEvaContext();
  const queryClient = useQueryClient();
  const isFeatureAccessoriesEnabled = useFeatureFlag('FEATURE_ACCESSORIES');
  const { country, language, locale } = useLocaleParams();
  const { mattressId, frameId, yearlySubscriptionId } =
    useResolvedCatalogProductIds();

  const countryCode = (country ?? '').toUpperCase();
  // Carts BFF expects a language code like "en", not a locale like "en-NL"
  const languageCode = language ?? locale?.split('-').at(0) ?? 'en';

  const {
    status: cartStatus,
    data,
    refetch: refetchCart,
  } = useBffShoppingCartQuery(
    { cartId: orderId ?? '', languageCode },
    {
      enabled: !!orderId,
    },
  );

  const cart = data?.data;

  const amounts = cart
    ? {
        currency: cart.currency,
        subtotalInclTax: cart.subtotal.amountInclTax,
        totalInclTax: cart.total.amountInclTax,
        totalDiscounts: cart.totalDiscounts,
        totalShipping: cart.totalShipping,
      }
    : undefined;

  const applyCartResponse = useCallback(
    (cartId: string, response: BffShoppingCartResponse) => {
      queryClient.setQueryData(
        shoppingCartQueryKey({ cartId, languageCode }),
        response,
      );
      return response;
    },
    [languageCode, queryClient],
  );

  const createOrder = useCallback(async () => {
    if (createOrderInFlight) {
      return createOrderInFlight;
    }

    if (!countryCode || !languageCode) {
      throw new Error('Missing locale countryCode/languageCode for cart creation');
    }

    createOrderInFlight = (async () => {
      try {
        const created = await createBffShoppingCart({
          body: { countryCode, languageCode },
        });
        // Seed cache before setOrderId so enabling the query does not GET an empty cart.
        queryClient.setQueryData(
          shoppingCartQueryKey({ cartId: created.data.id, languageCode }),
          created,
        );
        latestKnownCartId = created.data.id;
        setOrderId(created.data.id);
        markFreshOrderId(created.data.id);
        return created.data.id;
      } finally {
        createOrderInFlight = null;
      }
    })();

    return createOrderInFlight;
  }, [countryCode, languageCode, queryClient, setOrderId]);

  const refreshCartForId = useCallback(
    async (cartId: string) => {
      const key = shoppingCartQueryKey({ cartId, languageCode });
      // Mutations update the cart on the server; invalidate first so fetchQuery
      // does not return a still-fresh cached empty cart (default staleTime is 60s).
      await queryClient.invalidateQueries({ queryKey: key });
      return queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => fetchShoppingCart({ cartId, languageCode }),
      });
    },
    [languageCode, queryClient],
  );

  const refreshCart = useCallback(async () => {
    const cartId = orderId ?? latestKnownCartId;
    if (!cartId) return undefined;
    return refreshCartForId(cartId);
  }, [orderId, refreshCartForId]);

  const runWithStaleCartRecovery = useCallback(
    async <T>(
      operation: (cartId: string) => Promise<T>,
    ): Promise<{ result: T; cartId: string }> => {
      let activeCartId = orderId;
      if (!activeCartId) {
        activeCartId = await createOrder();
      }

      try {
        return { result: await operation(activeCartId), cartId: activeCartId };
      } catch (error) {
        if (!(await isInvalidShoppingCartError(error))) {
          throw error;
        }

        await invalidateStaleShoppingCart();
        latestKnownCartId = null;
        setOrderId(null);
        const newCartId = await createOrder();
        return { result: await operation(newCartId), cartId: newCartId };
      }
    },
    [createOrder, orderId, setOrderId],
  );

  const clearOrder = useCallback(() => {
    latestKnownCartId = null;
    setOrderId(null);
  }, [setOrderId]);

  const clearOrderIfEmpty = useCallback(
    (response: BffShoppingCartResponse) => {
      if (
        !response.data.products.length &&
        !response.data.subscriptions.length
      ) {
        clearOrder();
      }
    },
    [clearOrder],
  );

  const removeProduct = useCallback(
    async (productId: string) => {
      const { result, cartId } = await runWithStaleCartRecovery(
        async activeCartId => {
          const products = cart?.products ?? [];
          const subscriptions = cart?.subscriptions ?? [];

          const removeProductIds = new Set<string>([productId]);
          if (
            matchesMattressProductId(productId, mattressId) &&
            !isFeatureAccessoriesEnabled
          ) {
            const frameLine = products.find(p =>
              matchesFrameProductId(p.id, frameId),
            );
            if (frameLine) {
              removeProductIds.add(frameLine.id);
            }
          }

          let latest: BffShoppingCartResponse | undefined;
          if (
            matchesMattressProductId(productId, mattressId) &&
            isFeatureAccessoriesEnabled
          ) {
            if (
              yearlySubscriptionId &&
              subscriptions.some(s => s.id === yearlySubscriptionId)
            ) {
              latest = await putBffShoppingCartSubscriptions({
                cartId: activeCartId,
                body: {
                  subscriptions: [{ id: yearlySubscriptionId, quantity: 0 }],
                },
                languageCode,
              });
            }
          }

          if (removeProductIds.size) {
            latest = await putBffShoppingCartProducts({
              cartId: activeCartId,
              body: {
                products: Array.from(removeProductIds).map(id => ({
                  id,
                  quantity: 0,
                })),
              },
              languageCode,
            });
          }

          if (!latest) {
            throw new Error('No cart mutation performed for removeProduct');
          }
          return latest;
        },
      );

      applyCartResponse(cartId, result);
      clearOrderIfEmpty(result);
    },
    [
      applyCartResponse,
      clearOrderIfEmpty,
      isFeatureAccessoriesEnabled,
      cart?.products,
      cart?.subscriptions,
      mattressId,
      frameId,
      yearlySubscriptionId,
      languageCode,
      runWithStaleCartRecovery,
    ],
  );

  const { canAddFrames } = useFrameQuantityLimit(cart, frameId);

  const isProductAvailable = useCallback(
    (productId: string, quantity = 1) => {
      if (matchesFrameProductId(productId, frameId)) {
        return canAddFrames(quantity);
      }
      return Boolean(productId);
    },
    [canAddFrames, frameId],
  );

  const addProduct = useCallback(
    async (
      productId: string,
      quantity = 1,
      options?: CartMutationOptions,
    ) => {
      if (!isProductAvailable(productId, quantity)) return null;

      const { result, cartId } = await runWithStaleCartRecovery(
        async activeCartId =>
          putBffShoppingCartProducts({
            cartId: activeCartId,
            body: { products: [{ id: productId, quantity }] },
            languageCode,
          }),
      );

      if (!options?.skipRefresh) {
        applyCartResponse(cartId, result);
      }
      return productId;
    },
    [
      applyCartResponse,
      isProductAvailable,
      languageCode,
      runWithStaleCartRecovery,
    ],
  );

  const addSubscriptionProduct = useCallback(
    async (productId: string, options?: CartMutationOptions) => {
      const { result, cartId } = await runWithStaleCartRecovery(
        async activeCartId => {
          const currentSubscriptionInCart = cart?.subscriptions?.at(0);
          let latest: BffShoppingCartResponse | undefined;

          if (currentSubscriptionInCart) {
            latest = await putBffShoppingCartSubscriptions({
              cartId: activeCartId,
              body: {
                subscriptions: [
                  { id: currentSubscriptionInCart.id, quantity: 0 },
                ],
              },
              languageCode,
            });
          }

          if (productId !== NONE_SUBSCRIPTION_PRODUCT_ID) {
            latest = await putBffShoppingCartSubscriptions({
              cartId: activeCartId,
              body: { subscriptions: [{ id: productId, quantity: 1 }] },
              languageCode,
            });
          }

          return latest;
        },
      );

      if (
        productId !== NONE_SUBSCRIPTION_PRODUCT_ID &&
        result &&
        !options?.skipRefresh
      ) {
        applyCartResponse(cartId, result);
      }

      if (productId === NONE_SUBSCRIPTION_PRODUCT_ID) return null;
      return productId;
    },
    [
      applyCartResponse,
      cart?.subscriptions,
      languageCode,
      runWithStaleCartRecovery,
    ],
  );

  const modifyQuantity = useCallback(
    async (productId: string, quantity: number) => {
      const { result, cartId } = await runWithStaleCartRecovery(
        async activeCartId => {
          const isSubscriptionLine = cart?.subscriptions?.some(
            s => s.id === productId,
          );

          if (isSubscriptionLine) {
            return putBffShoppingCartSubscriptions({
              cartId: activeCartId,
              body: { subscriptions: [{ id: productId, quantity }] },
              languageCode,
            });
          }

          return putBffShoppingCartProducts({
            cartId: activeCartId,
            body: { products: [{ id: productId, quantity }] },
            languageCode,
          });
        },
      );

      applyCartResponse(cartId, result);
      clearOrderIfEmpty(result);
    },
    [
      applyCartResponse,
      clearOrderIfEmpty,
      languageCode,
      cart?.subscriptions,
      runWithStaleCartRecovery,
    ],
  );

  const addCoupon = useCallback(
    async (code: string) => {
      const { result, cartId } = await runWithStaleCartRecovery(
        async activeCartId =>
          putBffShoppingCartCoupon({
            cartId: activeCartId,
            body: { code },
            languageCode,
          }),
      );
      applyCartResponse(cartId, result);
      return true;
    },
    [applyCartResponse, languageCode, runWithStaleCartRecovery],
  );

  const removeCoupon = useCallback(async () => {
    const { result, cartId } = await runWithStaleCartRecovery(
      async activeCartId =>
        putBffShoppingCartCoupon({
          cartId: activeCartId,
          body: { code: '' },
          languageCode,
        }),
    );
    applyCartResponse(cartId, result);
  }, [applyCartResponse, languageCode, runWithStaleCartRecovery]);

  const duplicateOrder = useCallback(async () => {
    throw new Error('Duplicate cart is not supported with Shopping Carts BFF');
  }, []);

  // Disabled queries stay `pending` in TanStack Query v5; with no orderId there is
  // nothing to load — treat as success so empty-cart UI is not stuck on skeletons.
  const status: QueryStatus = orderId ? cartStatus : 'success';

  return {
    status,
    orderId,
    cart,
    amounts,
    setOrderId,
    createOrder,
    addProduct,
    addSubscriptionProduct,
    removeProduct,
    modifyQuantity,
    addCoupon,
    removeCoupon,
    clearOrder,
    refetchCart,
    refreshCart,
    duplicateOrder,
    isProductAvailable,
  };
};

export default useOrder;
